import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { heatmapService, statusConfig } from '../../services/heatmapService';
import LoadingState from './LoadingState';
import HeatMapSkeleton from './HeatMapSkeleton';
import ErrorState from './ErrorState';
import './HeatMap.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const HeatMap = () => {
  const [data, setData] = useState({ employees: [] });
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);
  
  // Состояние для тултипа
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    employee: null,
    training: null,
    status: null,
    trainingDetail: null
  });
  
  const isInitialMount = useRef(true);
  const isLoadingData = useRef(false);
  const dataLoaded = useRef(false);
  const [showSkeleton, setShowSkeleton] = useState(true);

  // Функция для форматирования вертикального текста в две строки
  const formatVerticalText = useCallback((text, maxLineLength = 12) => {
    if (!text) return '';
    
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';
    
    words.forEach(word => {
      // Проверяем, поместится ли слово в текущую строку
      const testLine = currentLine ? currentLine + ' ' + word : word;
      if (testLine.length <= maxLineLength) {
        currentLine = testLine;
      } else {
        // Сохраняем текущую строку и начинаем новую
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    
    // Добавляем последнюю строку
    if (currentLine) lines.push(currentLine);
    
    // Возвращаем React элементы с <br/> между строками
    return lines.map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  }, []);

  // Загрузка данных
  const loadData = useCallback(async (forceRefresh = false) => {
    if (isLoadingData.current) {
      return;
    }
    
    if (dataLoaded.current && !forceRefresh) {
      return;
    }
    
    isLoadingData.current = true;
    setLoading(true);
    setShowSkeleton(true);
    setError(null);
    
    try {
      const heatmapData = await heatmapService.getHeatmapData({}, forceRefresh);
      
      setTrainingTypes(heatmapData.trainingTypes || []);
      setCategories(heatmapData.categories || []);
      
      // Подразделения уже есть в ответе
      let departmentsArray = [];
      if (heatmapData.departments && Array.isArray(heatmapData.departments)) {
        departmentsArray = heatmapData.departments;
      } else {
        // Альтернативно - извлекаем из сотрудников
        const deptMap = new Map();
        heatmapData.employees?.forEach(employee => {
          if (employee.department && employee.department !== 'Не указано') {
            const deptId = employee.departmentId || String(employee.department).toLowerCase().replace(/\s/g, '_');
            if (!deptMap.has(deptId)) {
              deptMap.set(deptId, employee.department);
            }
          }
        });
        departmentsArray = Array.from(deptMap.entries()).map(([id, name]) => ({
          id: String(id),
          name
        })).sort((a, b) => a.name.localeCompare(b.name));
      }
      
      setDepartments(departmentsArray);
      
      setData({
        employees: heatmapData.employees || []
      });
      
      dataLoaded.current = true;
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
      setError(err.message || 'Не удалось загрузить данные тепловой карты');
    } finally {
      setLoading(false);
      setShowSkeleton(false);
      isLoadingData.current = false;
    }
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      loadData();
    }
  }, [loadData]);

  // Группировка trainingTypes по категориям
  const groupedTrainings = useMemo(() => {
    const groups = {};
    
    trainingTypes.forEach(training => {
      const categoryId = training.category_id || 'other';
      const categoryName = training.category_name || 'Без категории';
      
      if (!groups[categoryId]) {
        groups[categoryId] = {
          id: categoryId,
          name: categoryName,
          trainings: []
        };
      }
      groups[categoryId].trainings.push(training);
    });
    
    return Object.values(groups).sort((a, b) => {
      if (!isNaN(a.id) && !isNaN(b.id)) {
        return Number(a.id) - Number(b.id);
      }
      return a.name.localeCompare(b.name);
    });
  }, [trainingTypes]);

  // Фильтрация trainingTypes по категории
  const filteredGroupedTrainings = useMemo(() => {
    if (!selectedCategory) return groupedTrainings;
    
    return groupedTrainings.filter(group => 
      group.id.toString() === selectedCategory.toString()
    );
  }, [groupedTrainings, selectedCategory]);

  // Все отфильтрованные trainings для подсчета colspan
  const filteredTrainings = useMemo(() => {
    return filteredGroupedTrainings.reduce((acc, group) => {
      return acc.concat(group.trainings);
    }, []);
  }, [filteredGroupedTrainings]);

  // Получение статуса обучения
  const getTrainingStatus = (employee, courseId) => {
    if (employee.trainings && employee.trainings[courseId] !== undefined) {
      return employee.trainings[courseId];
    }
    return 'noData';
  };

  // Фильтрация сотрудников
  const filteredEmployees = useMemo(() => {
    let filtered = data.employees;
    
    // Фильтр по поиску
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(employee => 
        (employee.name && employee.name.toLowerCase().includes(term)) ||
        (employee.position && employee.position.toLowerCase().includes(term)) ||
        (employee.department && employee.department.toLowerCase().includes(term))
      );
    }
    
    // Фильтр по подразделению
    if (selectedDepartment) {
      filtered = filtered.filter(employee => {
        const employeeDeptId = employee.departmentId;
        const selectedDept = departments.find(d => String(d.id) === String(selectedDepartment));
        return String(employeeDeptId) === String(selectedDepartment) ||
               employee.department === selectedDept?.name;
      });
    }
    
    return filtered;
  }, [data.employees, searchTerm, selectedDepartment, departments]);

  const handleEmployeeClick = async (employee) => {
    setSelectedEmployee(employee);
    setShowModal(true);
    setLoadingEmployeeDetails(true);
    
    try {
      const details = await heatmapService.getEmployeeHeatmap(employee.id);
      setSelectedEmployeeDetails(details);
    } catch (err) {
      console.error('Ошибка загрузки деталей сотрудника:', err);
      setSelectedEmployeeDetails(null);
    } finally {
      setLoadingEmployeeDetails(false);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active': return 'color-active';
      case 'expiring': return 'color-expiring';
      case 'expired': return 'color-expired';
      case 'required': return 'color-required';
      default: return 'color-noData';
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedDepartment('');
  };

  const refreshData = async () => {
    await loadData(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusForModal = (status) => {
    return statusConfig[status] || statusConfig.noData;
  };

  const getTrainingDetail = (trainingId) => {
    if (!selectedEmployeeDetails?.trainings) return null;
    return selectedEmployeeDetails.trainings.find(t => t.id === trainingId || t.id?.toString() === trainingId);
  };

  const filteredTrainingsForModal = useMemo(() => {
    if (!selectedEmployee) return [];
    
    return filteredTrainings.filter(training => {
      const status = getTrainingStatus(selectedEmployee, training.id);
      return status !== 'noData';
    });
  }, [filteredTrainings, selectedEmployee]);

  const getCategoryColor = (categoryId) => {
    const colors = {
      1: '#dbeafe', 2: '#dcfce7', 3: '#fef3c7', 4: '#fce7f3',
      5: '#ede9fe', 6: '#ffedd5', 7: '#ccfbf1', 8: '#f1f5f9',
      9: '#fee2e2', 10: '#d1fae5'
    };
    return colors[categoryId] || '#f1f5f9';
  };

  const getCategoryTextColor = (categoryId) => {
    const colors = {
      1: '#1e40af', 2: '#166534', 3: '#92400e', 4: '#9d174d',
      5: '#5b21b6', 6: '#9a3412', 7: '#115e59', 8: '#334155',
      9: '#991b1b', 10: '#065f46'
    };
    return colors[categoryId] || '#334155';
  };

  // Обработчики для тултипа
  const getTrainingDetailForTooltip = useCallback(async (employeeId, trainingId) => {
    try {
      const details = await heatmapService.getEmployeeHeatmap(employeeId);
      const trainingDetail = details.trainings?.find(t => {
        const tId = t.id || t.trainingId || t.courseId;
        return tId?.toString() === trainingId?.toString();
      });
      
      if (trainingDetail) {
        return {
          completedDate: trainingDetail.completedDate || trainingDetail.completed_date || trainingDetail.completed_at,
          expiresDate: trainingDetail.expiresDate || trainingDetail.expires_date || trainingDetail.expiration_date,
          ...trainingDetail
        };
      }
      return null;
    } catch (err) {
      console.error('Ошибка загрузки деталей обучения:', err);
      return null;
    }
  }, []);

  const handleMouseEnter = async (e, employee, training, status) => {
    const rect = e.currentTarget.getBoundingClientRect();
    
    let trainingDetail = null;
    if (status !== 'noData' && status !== 'required') {
      trainingDetail = await getTrainingDetailForTooltip(employee.id, training.id);
    }
    
    // Определяем позицию тултипа, чтобы он не выходил за пределы экрана
    let x = rect.right + 10;
    let y = rect.top;
    
    if (x + 320 > window.innerWidth) {
      x = rect.left - 330;
    }
    
    if (y + 200 > window.innerHeight) {
      y = window.innerHeight - 210;
    }
    
    setTooltip({
      visible: true,
      x: x,
      y: y,
      employee,
      training,
      status,
      trainingDetail
    });
  };

  const handleMouseLeave = () => {
    setTooltip({
      ...tooltip,
      visible: false
    });
  };

  const renderTooltip = () => {
    if (!tooltip.visible || !tooltip.employee || !tooltip.training) return null;
    
    const { employee, training, status, trainingDetail } = tooltip;
    const config = getStatusForModal(status);
    
    const completedDate = trainingDetail?.completedDate ? formatDate(trainingDetail.completedDate) : null;
    const expiresDate = trainingDetail?.expiresDate ? formatDate(trainingDetail.expiresDate) : null;
    
    return (
      <div 
        className="heatmap-tooltip"
        style={{
          position: 'fixed',
          top: tooltip.y,
          left: tooltip.x,
          zIndex: 1000
        }}
      >
        <div className="tooltip-content">
          <div className="tooltip-header">
            <strong>{employee.name}</strong>
          </div>
          <div className="tooltip-divider"></div>
          <div className="tooltip-training-name">
            {training.name}
          </div>
          <div className="tooltip-status" style={{ color: config.color }}>
            {config.icon} {config.label}
          </div>
          {completedDate && (
            <div className="tooltip-date">
              📅 Пройдено: {completedDate}
            </div>
          )}
          {expiresDate && (
            <div className="tooltip-date">
              ⏰ Действует до: {expiresDate}
            </div>
          )}
          {status === 'required' && (
            <div className="tooltip-required">
              ⚠️ Требуется пройти обучение
            </div>
          )}
          {status === 'noData' && (
            <div className="tooltip-no-data">
              ❓ Нет данных о прохождении
            </div>
          )}
        </div>
      </div>
    );
  };


// функция экспорта в Excel
const exportToExcel = () => {
  try {
    // 1. Подготовка данных для Excel
    const exportData = prepareExportData();
    
    // 2. Создание workbook и worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // 3. Настройка ширины колонок
    const colWidths = [
      { wch: 30 }, // Сотрудник
      { wch: 25 }, // Должность
      { wch: 20 }, // Подразделение
      ...filteredTrainings.map(() => ({ wch: 15 })) // Обучения
    ];
    ws['!cols'] = colWidths;
    
    // 4. Стилизация заголовков (опционально)
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[address]) continue;
      ws[address].s = {
        font: { bold: true, sz: 12 },
        fill: { fgColor: { rgb: "4472C4" } },
        alignment: { horizontal: "center", vertical: "center" }
      };
    }
    
    // 5. Добавление листа и сохранение
    XLSX.utils.book_append_sheet(wb, ws, "Тепловая карта");
    
    // 6. Сохранение файла
    const fileName = `Тепловая_карта_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
  } catch (error) {
    console.error('Ошибка при экспорте в Excel:', error);
    alert('Ошибка при экспорте данных. Попробуйте позже.');
  }
};

// Функция подготовки данных для экспорта
const prepareExportData = () => {
  const exportRows = [];
  
  // Для каждого сотрудника создаем строку
  filteredEmployees.forEach(employee => {
    const row = {
      'Сотрудник': employee.name,
      'Должность': employee.position,
      'Подразделение': employee.department || 'Не указано'
    };
    
    // Добавляем колонку для каждого обучения с названием из training.name
    filteredTrainings.forEach(training => {
      const status = getTrainingStatus(employee, training.id);
      const statusInfo = statusConfig[status] || statusConfig.noData;
      // Используем название курса как ключ (или как значение в объекте)
      row[training.name] = `${statusInfo.icon} ${statusInfo.label}`;
    });
    
    exportRows.push(row);
  });
  
  return exportRows;
};
// функция экспорта в Excel

  const hasActiveFilters = searchTerm || selectedCategory || selectedDepartment;

  if (showSkeleton && data.employees.length === 0 && !error) {
    return <HeatMapSkeleton />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refreshData} />;
  }

  if (trainingTypes.length === 0 && data.employees.length === 0) {
    return <HeatMapSkeleton />;
  }

  if (trainingTypes.length === 0) {
    return <LoadingState message="Загрузка типов обучений..." />;
  }

  return (
    <div className="heatmap-container">
      <div className="heatmap-header">
        <h1>Тепловая карта компетенций</h1>
        <p className="subtitle">Актуальность допусков и обучений сотрудников</p>
      </div>

      {/* Легенда */}
       <div className="legend">
          <h3>Легенда</h3>
            <div className="legend-items">
              {Object.entries(statusConfig)
                .filter(([key]) => key !== 'noData')  // Фильтруем 'noData'
                .map(([key, config]) => (
                  <div key={key} className="legend-item">
                    <div className={`legend-color ${key}-color`} style={{ backgroundColor: config.color }}></div>
                    <span className="legend-label">{config.label}</span>
                  </div>
                ))}
            </div>
      </div>

      {/* Фильтры */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Поиск по ФИО, должности или подразделению..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {/* Фильтр по подразделениям */}
        <div className="filter-group">
          <label>Подразделение:</label>
          <select 
            value={selectedDepartment} 
            onChange={(e) => setSelectedDepartment(e.target.value)}
          >
            <option value="">Все подразделения</option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>
        </div>
        
        {/* Фильтр по категориям */}
        <div className="filter-group">
          <label>Категория:</label>
          <select 
            value={selectedCategory} 
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Все категории</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          
        </div>
        
        <button className="btn-secondary btn-sm" onClick={exportToExcel} title="Экспорт в Excel">
            📊 Экспорт в Excel
          </button>
        <button className="btn-secondary btn-sm" onClick={refreshData} title="Обновить данные">
          🔄 Обновить
        </button>
      </div>

      {/* Статистика фильтрации */}
      {hasActiveFilters && (
        <div className="filter-stats">
          <span className="filter-stats-text">
            Отфильтровано: 
            {selectedDepartment && (
              <span className="filter-badge">
                Подразделение: {departments.find(d => String(d.id) === String(selectedDepartment))?.name}
              </span>
            )}
            {selectedCategory && (
              <span className="filter-badge">
                Категория: {categories.find(c => String(c.id) === String(selectedCategory))?.name}
              </span>
            )}
            {searchTerm && (
              <span className="filter-badge">
                Поиск: "{searchTerm}"
              </span>
            )}
          </span>
          <button className="reset-filters-btn" onClick={resetFilters}>
            ✕ Сбросить все
          </button>
        </div>
      )}

      {/* Таблица с группировкой по категориям */}
      <div className="heatmap-table-wrapper">
        <table className="heatmap-table">
          <thead>
            {/* Первая строка - названия курсов с вертикальным текстом в две строки */}
            <tr>
              <th className="employee-col" rowSpan="2">Сотрудник / Должность / Подразделение</th>
              {filteredGroupedTrainings.map((group, groupIndex) => 
                group.trainings.map((training, courseIndex) => (
                  <th 
                    key={training.id} 
                    className="training-col-vertical" 
                    title={training.name}
                    data-category-boundary={courseIndex === 0 && groupIndex > 0 ? "true" : "false"}
                    data-category-id={courseIndex === 0 && groupIndex > 0 ? group.id : undefined}
                  >
                    <div className="vertical-text">
                      {formatVerticalText(training.name, 30)}
                    </div>
                  </th>
                ))
              )}
            </tr>
            
            {/* Вторая строка - категории с цветовой индикацией */}
            <tr>
              {filteredGroupedTrainings.map((group) => (
                <th 
                  key={`cat-${group.id}`} 
                  colSpan={group.trainings.length} 
                  className="category-header-compact"
                  data-category={group.id}
                  style={{
                    backgroundColor: getCategoryColor(group.id),
                    color: getCategoryTextColor(group.id)
                  }}
                >
                  {group.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map(employee => (
              <tr 
                key={employee.id} 
                className="employee-row"
                onClick={() => handleEmployeeClick(employee)}
              >
                <td className="employee-info employee-col">
                  <div className="employee-name">{employee.name}</div>
                  <div className="employee-position">{employee.position}</div>
                  {employee.department && employee.department !== 'Не указано' && (
                    <div className="employee-department">📍 {employee.department}</div>
                  )}
                </td>
                {filteredGroupedTrainings.map((group, groupIndex) =>
                  group.trainings.map((training, courseIndex) => {
                    const status = getTrainingStatus(employee, training.id);
                    const statusClass = getStatusClass(status);
                    return (
                      <td 
                        key={training.id} 
                        className={`color-cell ${statusClass}`}
                        data-category-boundary={courseIndex === 0 && groupIndex > 0 ? "true" : "false"}
                        data-category-id={courseIndex === 0 && groupIndex > 0 ? group.id : undefined}
                        onMouseEnter={(e) => handleMouseEnter(e, employee, training, status)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <div className="color-square" />
                      </td>
                    );
                  })
                )}
              </tr>
            ))}
            {filteredEmployees.length === 0 && (
              <tr>
                <td colSpan={filteredTrainings.length + 1} style={{ textAlign: 'center', padding: '40px' }}>
                  <div>Нет данных, соответствующих фильтрам</div>
                  <button 
                    className="btn-secondary" 
                    onClick={resetFilters}
                    style={{ marginTop: '12px' }}
                  >
                    Сбросить фильтры
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Тултип */}
      {renderTooltip()}

      {/* Модальное окно */}
      {showModal && selectedEmployee && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedEmployee.name}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="modal-position">
                {selectedEmployee.position}
              </div>
              {selectedEmployee.department && selectedEmployee.department !== 'Не указано' && (
                <div className="modal-department">
                  📍 {selectedEmployee.department}
                </div>
              )}
              
              {/* Статистика */}
              {selectedEmployeeDetails?.statistics && (
                <div className="modal-statistics">
                  <h3>Статистика</h3>
                  <div className="statistics-grid">
                    <div className="stat-item">
                      <span className="stat-label">Всего:</span>
                      <span className="stat-value">{selectedEmployeeDetails.statistics.total}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label text-green">Действует:</span>
                      <span className="stat-value text-green">{selectedEmployeeDetails.statistics.active}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label text-amber">Истекает:</span>
                      <span className="stat-value text-amber">{selectedEmployeeDetails.statistics.expiring}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label text-red">Просрочено:</span>
                      <span className="stat-value text-red">{selectedEmployeeDetails.statistics.expired}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label text-purple">Требуется:</span>
                      <span className="stat-value text-purple">{selectedEmployeeDetails.statistics.required}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label text-gray">Нет данных:</span>
                      <span className="stat-value text-gray">{selectedEmployeeDetails.statistics.noData}</span>
                    </div>
                  </div>
                  <div className="compliance-bar">
                    <div 
                      className="compliance-fill"
                      style={{ width: `${selectedEmployeeDetails.compliancePercentage || 0}%` }}
                    />
                    <span className="compliance-text">
                      Соответствие: {selectedEmployeeDetails.compliancePercentage || 0}%
                    </span>
                  </div>
                </div>
              )}
              
              {/* Обучения и допуски */}
              <div className="modal-trainings">
                <h3>Обучения и допуски</h3>
                {loadingEmployeeDetails ? (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Загрузка деталей...</p>
                  </div>
                ) : filteredTrainingsForModal.length === 0 ? (
                  <div className="empty-trainings">
                    <p>Нет данных об обучении</p>
                  </div>
                ) : (
                  filteredTrainingsForModal.map(training => {
                    const status = getTrainingStatus(selectedEmployee, training.id);
                    const config = getStatusForModal(status);
                    const trainingDetail = getTrainingDetail(training.id);
                    
                    return (
                      <div key={training.id} className="modal-training-item">
                        <div className="training-name-cell">{training.name}</div>
                        <div className="training-info-cell">
                          <span 
                            className="training-status-cell"
                            style={{ 
                              backgroundColor: config.bgColor, 
                              color: config.color 
                            }}
                          >
                            {config.icon} {config.label}
                          </span>
                          {trainingDetail?.expiresDate && (
                            <span className="training-expiry-date">
                              до {formatDate(trainingDetail.expiresDate)}
                            </span>
                          )}
                          {trainingDetail?.completedDate && !trainingDetail?.expiresDate && (
                            <span className="training-completed-date">
                              пройдено {formatDate(trainingDetail.completedDate)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeatMap;